import React from "react";
import * as Sentry from "@sentry/react";
import "./app-error.css";

/* The crash screen sits ABOVE ThemeSwitcherProvider, Redux and the Router (see
   `src/App.js`), so it must not depend on the app it is reporting on: no AntD
   components, no router links, no theme stylesheet. Plain markup, inline SVG
   glyphs and `app-error.css` only, the same discipline `UpdateNotification`
   follows. Every probe below is best-effort and falls back to "unknown". */

const MANIFEST_URL = "/asset-manifest.json";
const PROBE_TIMEOUT_MS = 6000;
const COPIED_RESET_MS = 2400;

const AlertGlyph = () => (
  <svg
    width={26}
    height={26}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10.3 3.6 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.6a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
);

/******************************************************************
 * ##: Read the build currently served
 * Fetches the CRA asset manifest, bypassing the cache, and returns the entry
 * bundle filename the server is handing out right now.
 * @param {AbortSignal} signal - Aborts the request once the probe times out
 * @returns {Promise<string|null>} - The entry filename, or null when unreadable
 * History:
 * 29-08-2026: Created
 ******************************************************************/
const fetchServedBundle = async (signal) => {
  const response = await fetch(`${MANIFEST_URL}?_=${Date.now()}`, {
    cache: "no-store",
    signal,
  });
  if (!response.ok) return null;

  const manifest = await response.json();
  // CRA 5 writes `files["main.js"]`; `entrypoints` is the fallback shape.
  const entry =
    manifest?.files?.["main.js"] ||
    (Array.isArray(manifest?.entrypoints) ? manifest.entrypoints.find((file) => /main[.\w-]*\.js$/.test(file)) : null);

  return typeof entry === "string" ? entry.split("/").pop() : null;
};

/******************************************************************
 * ##: Read the build this tab is running
 * Looks up the entry bundle filename among the scripts already in the document.
 * @param {string} servedBundle - The filename the server currently serves
 * @returns {string|null} - The matching loaded filename, or null when absent
 * History:
 * 29-08-2026: Created
 ******************************************************************/
const findLoadedBundle = (servedBundle) => {
  const sources = Array.from(document.querySelectorAll("script[src]")).map((script) => (script.getAttribute("src") || "").split("?")[0]);
  return sources.find((source) => source.endsWith(`/${servedBundle}`) || source === servedBundle) || null;
};

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      reference: null,
      // "checking" -> "ok" | "offline" | "unknown"
      connection: "checking",
      // "checking" -> "current" | "stale" | "unknown"
      build: "checking",
      bundle: null,
      isRecovering: false,
      hasCopied: false,
    };
    this.isLive = false;
    this.copyTimer = null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidMount() {
    this.isLive = true;
  }

  componentWillUnmount() {
    this.isLive = false;
    if (this.copyTimer) window.clearTimeout(this.copyTimer);
  }

  componentDidCatch(error, errorInfo) {
    console.error("App crash:", error, errorInfo);

    // React swallows errors a boundary catches, so without this the crash never
    // reaches Sentry and the reference shown to the producer would point nowhere.
    try {
      const reference = Sentry.captureException(error, {
        contexts: { react: { componentStack: errorInfo?.componentStack } },
      });
      this.safeSetState({ reference: reference || null });
    } catch (captureError) {
      console.error("App crash: could not report to Sentry", captureError);
    }

    this.runDiagnostics();
  }

  /******************************************************************
   * ##: Guarded state update
   * Applies a state patch only while the boundary is still mounted.
   * @param {object} patch - The partial state to merge
   * @returns {void} - Nothing
   * History:
   * 29-08-2026: Created
   ******************************************************************/
  safeSetState = (patch) => {
    if (this.isLive) this.setState(patch);
  };

  /******************************************************************
   * ##: Diagnose the failure
   * Checks the connection and whether this tab is running the build the server
   * serves, so the screen names the likely cause instead of listing every one.
   * @returns {Promise<void>} - Resolves once both checks settle
   * History:
   * 29-08-2026: Created
   ******************************************************************/
  runDiagnostics = async () => {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      this.safeSetState({ connection: "offline", build: "unknown" });
      return;
    }

    const controller = typeof AbortController === "function" ? new AbortController() : null;
    const timeoutId = window.setTimeout(() => controller && controller.abort(), PROBE_TIMEOUT_MS);

    try {
      const servedBundle = await fetchServedBundle(controller ? controller.signal : undefined);
      // The manifest answered, so the connection is up whatever it contained.
      if (!servedBundle) {
        this.safeSetState({ connection: "ok", build: "unknown" });
        return;
      }

      const loadedBundle = findLoadedBundle(servedBundle);
      this.safeSetState({
        connection: "ok",
        build: loadedBundle ? "current" : "stale",
        bundle: loadedBundle ? servedBundle : `${servedBundle} (servido)`,
      });
    } catch (probeError) {
      // A dead probe is only evidence of the connection when the browser agrees.
      const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;
      this.safeSetState({
        connection: isOffline ? "offline" : "unknown",
        build: "unknown",
      });
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  /******************************************************************
   * ##: Recommended recovery plan
   * Turns the two probe results into the culprit row, the guidance line and the
   * primary action label, so the rail, the copy and the button always agree.
   * @returns {object} - { fault, note, actionLabel }
   * History:
   * 29-08-2026: Created
   ******************************************************************/
  getPlan = () => {
    const { connection, build } = this.state;

    if (connection === "checking" || build === "checking") {
      return {
        fault: null,
        note: "A verificar a ligação e a versão da aplicação.",
        actionLabel: "Recarregar esta página",
      };
    }

    if (connection === "offline") {
      return {
        fault: "connection",
        note: "Sem ligação à internet a aplicação não carrega. Verifica a rede e tenta de novo.",
        actionLabel: "Tentar novamente",
      };
    }

    if (build === "stale") {
      return {
        fault: "build",
        note: "Ficaste numa versão antiga da aplicação, que é a causa mais provável desta falha. Atualiza para ires buscar a mais recente.",
        actionLabel: "Atualizar para a versão mais recente",
      };
    }

    return {
      fault: "page",
      note: "A ligação e a versão estão bem, por isso a falha é desta página. Recarrega para a carregar de raiz; se voltar a acontecer, envia a referência ao suporte.",
      actionLabel: "Recarregar esta página",
    };
  };

  /******************************************************************
   * ##: Recover the app
   * Does the hard refresh the old screen asked the producer to type by hand:
   * drops any service worker and cached response, then reloads.
   * @returns {Promise<void>} - Resolves when the reload is triggered
   * History:
   * 29-08-2026: Created
   ******************************************************************/
  handleRecover = async () => {
    this.setState({ isRecovering: true });

    try {
      if (typeof navigator !== "undefined" && navigator.serviceWorker) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.unregister()));
      }
    } catch (workerError) {
      console.error("App crash: could not unregister service workers", workerError);
    }

    try {
      if (typeof caches !== "undefined") {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
    } catch (cacheError) {
      console.error("App crash: could not clear caches", cacheError);
    }

    window.location.reload();
  };

  /******************************************************************
   * ##: Build the support payload
   * Assembles the technical detail support needs to trace the crash. ISO time on
   * purpose: this text is pasted into a ticket, not read on screen.
   * @returns {string} - The multi-line detail block
   * History:
   * 29-08-2026: Created
   ******************************************************************/
  buildDetails = () => {
    const { error, reference, bundle, connection, build } = this.state;

    return [
      "SalesPark - detalhes do erro",
      `Referência: ${reference || "sem referência"}`,
      `Build: ${bundle || "desconhecida"}`,
      `Versão: ${build}`,
      `Ligação: ${connection}`,
      `Página: ${window.location.pathname}${window.location.search}`,
      `Data: ${new Date().toISOString()}`,
      `Erro: ${error?.name || "Error"}: ${error?.message || "sem mensagem"}`,
    ].join("\n");
  };

  /******************************************************************
   * ##: Copy the support payload
   * Puts the detail block on the clipboard, with the legacy path as a fallback
   * for browsers that withhold the async clipboard outside a secure context.
   * @returns {Promise<void>} - Resolves once the copy is attempted
   * History:
   * 29-08-2026: Created
   ******************************************************************/
  handleCopyDetails = async () => {
    const details = this.buildDetails();

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(details);
      } else {
        const holder = document.createElement("textarea");
        holder.value = details;
        holder.setAttribute("readonly", "");
        holder.style.position = "fixed";
        holder.style.opacity = "0";
        document.body.appendChild(holder);
        holder.select();
        document.execCommand("copy");
        document.body.removeChild(holder);
      }

      this.safeSetState({ hasCopied: true });
      if (this.copyTimer) window.clearTimeout(this.copyTimer);
      this.copyTimer = window.setTimeout(() => this.safeSetState({ hasCopied: false }), COPIED_RESET_MS);
    } catch (copyError) {
      console.error("App crash: could not copy the error details", copyError);
    }
  };

  /******************************************************************
   * ##: Render one diagnosis row
   * Draws a rail row: the pip, the check name and its verdict, tinted when this
   * is the row the recommended action is about.
   * @param {object} check - { key, label, value, tone, isBusy }
   * @param {string} fault - The key of the culprit row, if the probes settled
   * @returns {JSX.Element} - The list item
   * History:
   * 29-08-2026: Created
   ******************************************************************/
  renderCheck = (check, fault) => {
    const classNames = ["aerr-check", `aerr-check--${check.tone}`, check.isBusy ? "aerr-check--busy" : "aerr-check--done"];
    if (fault === check.key) classNames.push("aerr-check--fault");

    return (
      <li className={classNames.join(" ")} key={check.key}>
        <span className="aerr-pip" aria-hidden="true" />
        <span className="aerr-check-label">{check.label}</span>
        <span className="aerr-check-value">{check.value}</span>
      </li>
    );
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { connection, build, reference, isRecovering, hasCopied } = this.state;
    const plan = this.getPlan();

    const connectionCheck = {
      key: "connection",
      label: "Ligação à internet",
      isBusy: connection === "checking",
      tone: {
        checking: "neutral",
        ok: "ok",
        offline: "bad",
        unknown: "neutral",
      }[connection],
      value: {
        checking: "A verificar",
        ok: "Ativa",
        offline: "Sem ligação",
        unknown: "Não foi possível verificar",
      }[connection],
    };

    const buildCheck = {
      key: "build",
      label: "Versão da aplicação",
      isBusy: build === "checking",
      tone: {
        checking: "neutral",
        current: "ok",
        stale: "warn",
        unknown: "neutral",
      }[build],
      value: {
        checking: "A verificar",
        current: "Atualizada",
        stale: "Desatualizada",
        unknown: "Não foi possível verificar",
      }[build],
    };

    // Red only while this row IS the culprit: the crash is a given, so painting it
    // red next to a resolved cause would out-shout the row the producer must act on.
    const pageCheck = {
      key: "page",
      label: "Esta página",
      isBusy: false,
      tone: plan.fault === "page" ? "bad" : "neutral",
      value: "Falhou ao carregar",
    };

    return (
      <div className="aerr-page">
        <div className="aerr-shell">
          <img className="aerr-brand" src="/img/logo_grey.svg" alt="SalesPark" />

          <div className="aerr-panel">
            <div className="aerr-head">
              <div className="aerr-tile">
                <AlertGlyph />
              </div>
              <div>
                <div className="aerr-eyebrow">Erro da aplicação</div>
                <h1 className="aerr-title">Esta página deixou de responder.</h1>
                <p className="aerr-sub">Isto é uma falha desta página, não dos teus dados. O que já tinhas guardado está seguro.</p>
              </div>
            </div>

            <hr className="aerr-rule" />

            <ul className="aerr-checks">{[connectionCheck, buildCheck, pageCheck].map((check) => this.renderCheck(check, plan.fault))}</ul>

            <p className="aerr-verdict">{plan.note}</p>

            <div className="aerr-actions">
              <button type="button" className="sp-btn" onClick={this.handleRecover} disabled={isRecovering}>
                {isRecovering ? "A recarregar..." : plan.actionLabel}
              </button>
              <a className="sp-btn sp-btn-neutral" href="/dashboard">
                Voltar ao início
              </a>
            </div>
          </div>

          <div className="aerr-foot">
            {!!reference && (
              <>
                <span>
                  Referência <span className="aerr-ref">{reference.slice(0, 8)}</span>
                </span>
                <span className="aerr-dot">&middot;</span>
              </>
            )}
            <button type="button" className={`aerr-linkbtn${hasCopied ? " aerr-linkbtn--done" : ""}`} onClick={this.handleCopyDetails}>
              {hasCopied ? "Detalhes copiados" : "Copiar detalhes para o suporte"}
            </button>
            <span className="aerr-dot">&middot;</span>
            <a className="aerr-linkbtn" href="/support-request">
              Falar com o suporte
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
